/**
 * AdminSubNav — left-side sub-navigation for admin mode.
 *
 * Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — sub-child icons
 * restored with UNIQUE + LOGICAL vocabulary. Push 32 walkback:
 *   Ean gut-check ("No I like the sub tab icons, they look great on
 *   capabilities and permissions, what I didn't like is that the sub
 *   child items icons were the same when they should be unique and
 *   logical"). Push 32 had killed all child icons because the four
 *   Analytics children were all `iconKind: 'activity'` (IconSparkle
 *   duplicated four times), which read as visual noise. The right fix
 *   is per-child unique icons, not text-only rows. This push implements
 *   that fix.
 *
 * Child icon assignments (all obsidian tone via nav override):
 *   Capabilities ▾:
 *     Models      → IconBolt   (kind='models')      — AI engine
 *     Connectors  → IconPlug   (kind='connectors')  — plugs into apps
 *     Prompts     → IconWave   (kind='prompts')     — prompt = message-pattern
 *     Library     → IconLibrary (kind='library')    — shelf of docs
 *     Market      → IconGrid   (kind='catalog')     — grid of tiles
 *   Permissions ▾ (parent IconLock stays):
 *     Permission Sets → IconShield (kind='permission-set-shield')
 *     Assignments     → IconUserCheck (kind='assignment-link', Push 52
 *                                       swap from IconLink — user-check
 *                                       reads more clearly as "assigned")
 *   Analytics ▾ (parent IconBarChart, Sub-agent TTTTT 3-tab rebuild
 *                collapsed the prior 4-child IA — Overview + Insights had
 *                been embedding the same AdminInsights body twice; Spend
 *                mis-labeled the by-tag + by-day pivots):
 *     Overview → IconDashboard  (kind='dashboard')   — aggregate panels
 *     Reports  → IconCoin       (kind='token-debit') — dimensional spend breakdown
 *     Activity → IconPulse      (kind='pulse')       — heartbeat monitor
 *
 * Invariants respected:
 *   - No two admin sub-nav children share an icon.
 *   - No child has the same icon as its parent.
 *   - Same-concept icons stay consistent across the app:
 *     * IconLibrary here matches the Library user-rail row.
 *     * IconGrid here matches Market/Catalog everywhere.
 *     * IconSparkle here matches every other "AI-generated" surface.
 *
 * Prior Push 5-post-smoke-32 (Sub-agent U, 2026-07-15) — IA doctrine lock +
 * duplicative-icon kill.
 *
 *   IA doctrine (adjudicated from Ean's "we need to come up with a
 *   philosophy of when something earns a dropdown and independent pages
 *   vs sub nav items" gut check):
 *
 *     - Leaf-with-tabs when children are ASPECTS of ONE thing that admin
 *       visits together in one session. Sidebar shows one item; the
 *       PAGE holds tabs.
 *     - Parent-with-children when children are INDEPENDENT destinations
 *       admin visits separately. Sidebar has a ▾ dropdown; each child
 *       is its own route.
 *
 *   Applied:
 *     Home             — pure leaf (orient)
 *     Workspace        — leaf-with-tabs (General/Usage/Billing/Notifications
 *                        are aspects of one workspace; the tabs live on
 *                        the /admin/workspace page)
 *     People           — leaf-with-tabs (Users/Invitations/Approvals)
 *     Capabilities ▾   — parent-with-children (Models/Connectors/Prompts/
 *                        Library/Market are INDEPENDENT capability types)
 *     Permissions ▾    — parent-with-children (Permission Sets · Assignments)
 *     Analytics ▾      — parent-with-children (Overview/Spend/Activity/
 *                        Insights are independent report destinations)
 *
 *   Icon rule (from same push):
 *     - Top-level rows keep their ObjectIcon tile (wayfinding anchor).
 *     - Sub-nav CHILD rows render TEXT ONLY — no duplicate icon tile.
 *       Kills the visual noise Ean flagged ("The icons across sections
 *       in admin are duplicated"). Shared-concept icons (bell for
 *       notifications) still appear inside pages / TopNav / user surfaces
 *       where the concept lives, just not as sidebar-row duplicates.
 *     - Workspace icon swapped from `settings` (gear) to `workspace`
 *       (briefcase). Gear survives as chat-settings glyph; the Workspace
 *       concept is broader than "preferences" so the briefcase reads truer.
 *
 * Prior Push 5-post-smoke-20 (2026-07-14) split Workspace into a
 * parent-with-children shape (General/Usage/Billing/Notifications each a
 * child route). This push walks it back to leaf-with-tabs — Ean's
 * directive: "The workspace has a dropdown and the sub nav items, we
 * need to pick one; my gut says we should have workspace be the 4 sub
 * tabs." Same underlying routes still work (backward-compat NavLinks
 * from admin home + Cmd-K still resolve), but the sidebar surfaces
 * only the parent.
 *
 * D-196 vocabulary walkback: "Usage" was retired from admin surfaces in
 * a prior push. Push 5-post-smoke-20 walks that back for this label + the
 * money-consumption page under it; it's the natural admin word for what
 * lives there. Not a vocab-wide reversal — narrow to this surface.
 *
 * Prior Push 5-post-smoke-19 (2026-07-14) split Analytics ▾ from Activity
 * leaf. Prior Push 5-post-smoke-4 (2026-07-11) collapsed Insights into an
 * Activity leaf tab.
 *
 * Prior Push 5-post-smoke-4 (2026-07-11) collapsed Insights into an
 * Activity leaf tab; this push pulls the whole analytics bucket out into
 * its own top-level so admins mentally partition "manage" (top 5) from
 * "look at reports" (Analytics). D-163 re-locked at 6 items.
 *
 * Push #14 polish:
 *   - ObjectIcon on every item (xs / 20px) flush-left of the label
 *   - Chevron right-aligned (trailing affordance, not leading)
 *   - Active state: 3px teal left-bar + teal label + soft teal tint
 *   - Parent-active hint: teal label, no left-bar (signals "child of mine is
 *     active" without competing with the leaf's own bar)
 *   - Users count chip → teal-tinted pill (matches dashboard card chips)
 *   - Implicit section spacers via extra vertical gap between functional
 *     zones (no group headers, no dividers — pure whitespace hierarchy)
 *
 * URL stability lock (Ean directive 2026-06-10): sidebar labels are
 * user-facing copy, URLs and code symbols are implementation. They
 * intentionally do NOT homogenize. Specifically:
 *   - Sidebar label "Users" → URL stays `/admin/people` → code stays PersonDetail
 *   - Sidebar label "Prompts" → URL stays `/admin/prompts`
 *   - Sidebar label "Models" → URL stays `/admin/models`
 * Three independent layers. Don't sweep to align them.
 *
 * Counts: pending_invitations + open_requests roll up onto the Users leaf
 * (both surface inside the Users page tabs).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import type { Role } from '../lib/types.ts';
import { fetchAdminOverview, ApiError } from '../lib/api.ts';
import { ObjectIcon, type ObjectKind } from './ObjectIcon.tsx';

interface AdminSubNavProps {
  role: Role;
}

type CountKey = 'pending_invitations' | 'open_requests';

interface LeafNode {
  kind: 'leaf';
  label: string;
  /** ObjectIcon kind for the leading tile. */
  iconKind: ObjectKind;
  to: string;
  end?: boolean;
  ownerOnly?: boolean;
  title?: string;
  countKeys?: CountKey[];
  /** When set, this leaf begins a new visual zone — extra top margin
   *  inserts an implicit section spacer (no label, no divider). */
  zoneStart?: boolean;
}

interface ParentNode {
  kind: 'parent';
  label: string;
  iconKind: ObjectKind;
  id: string;
  title?: string;
  children: LeafNode[];
  zoneStart?: boolean;
}

type SidebarNode = LeafNode | ParentNode;

/**
 * Push 5-post-smoke-32 (Sub-agent U, 2026-07-15) — 6-item IA:
 *   1. Home          — orient (pure leaf)
 *   2. Workspace     — knobs + money (leaf; tabs on the page)
 *   3. People        — who (leaf with tabs)
 *   4. Capabilities  — what (parent w/ children)
 *   5. Permissions   — who-can-do-what (parent w/ children)
 *   6. Analytics     — insights (parent w/ children: Overview / Spend / Activity / Insights)
 *
 * Icon color: ALL admin nav icons render in obsidian per Push #21. The
 * sidebar's job is wayfinding, not meaning encoding — a unified base color
 * is honest. Per-surface icon colors (teal for action, obsidian for
 * governance) stay live INSIDE the page body via ObjectIcon's REGISTRY.
 */
const TREE: SidebarNode[] = [
  {
    kind: 'leaf',
    label: 'Home',
    iconKind: 'home',
    to: '/admin',
    end: true,
    title: 'Workspace summary at a glance',
  },
  {
    // Push 5-post-smoke-32 (Sub-agent U, 2026-07-15) — Workspace back
    // to leaf. The 4 sub-surfaces (General/Usage/Billing/Notifications)
    // are aspects of one workspace, so they live as tabs on the
    // /admin/workspace page rather than as sidebar children. Route
    // aliases (/admin/workspace/{general,usage,billing,notifications})
    // continue to resolve via Settings.tsx path-based routing so
    // bookmarks + Cmd-K deep-links keep working.
    //
    // Icon swap: was 'settings' (gear). Now 'workspace' (briefcase) —
    // Ean's gear-still-ugly gut check + the concept is broader than
    // "preferences" anyway.
    kind: 'leaf',
    label: 'Workspace',
    iconKind: 'workspace',
    to: '/admin/workspace',
    end: true,
    title: 'General preferences, usage + credit knobs, billing, and notifications',
  },
  {
    kind: 'leaf',
    label: 'People',
    iconKind: 'people',
    to: '/admin/people',
    title: 'Your team, invitations, and pending approvals',
    countKeys: ['pending_invitations', 'open_requests'],
  },
  {
    kind: 'parent',
    label: 'Capabilities',
    iconKind: 'capabilities',
    id: 'capabilities',
    title: 'What can be done',
    children: [
      // D-167: Models → Connectors → Prompts → Library → Market
      { kind: 'leaf', label: 'Models',     iconKind: 'models',     to: '/admin/models',     title: 'AI models available to your team' },
      { kind: 'leaf', label: 'Connectors', iconKind: 'connectors', to: '/admin/connectors', title: 'Apps your team can use' },
      { kind: 'leaf', label: 'Prompts',    iconKind: 'prompts',    to: '/admin/prompts',    title: 'Prompt templates installed' },
      { kind: 'leaf', label: 'Library',    iconKind: 'library',    to: '/admin/library',    title: 'Documents and folders your team can reference' },
      { kind: 'leaf', label: 'Market',     iconKind: 'catalog',    to: '/admin/market',    title: "Browse Olytic's public market. Install or request new" },
    ],
  },
  {
    kind: 'parent',
    label: 'Permissions',
    iconKind: 'permission-sets',
    id: 'permissions',
    title: 'Who can do what',
    children: [
      // Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — unique child
      // icons per Ean directive. Sets = shield (protection bundle);
      // Assignments = user-check (person with a checkmark — reads as
      // "assigned"). Push 52 swap from the prior chain-link glyph after
      // Ean's smoke walk: "I'm not really sure what the assignments one is."
      {
        // Sub-agent WWWWW (2026-07-31) — sentence case normalization
        // ("Permission Sets" → "Permission sets"). ObjectIcon label +
        // AdminHome tile + PersonDetail summary already used sentence
        // case; sidenav + list-view hero title + breadcrumb + nav search
        // + inline links were the outliers. Now uniform.
        kind: 'leaf',
        label: 'Permission sets',
        iconKind: 'permission-set-shield',
        to: '/admin/permission-sets',
        title: 'Bundle features into reusable roles',
      },
      {
        kind: 'leaf',
        label: 'Assignments',
        iconKind: 'assignment-link',
        to: '/admin/assignments',
        title: 'Quick grants + recent changes (the relief valve)',
      },
    ],
  },
  {
    kind: 'parent',
    label: 'Analytics',
    iconKind: 'analytics',
    id: 'analytics',
    title: 'Dashboard, drilldown reports, and the raw event log',
    children: [
      // Sub-agent TTTTT (2026-07-31) — 3-child IA per the 60k-admin test:
      //   Overview → dashboard (daily-check page — charts + KPIs + callouts)
      //   Reports  → drilldown pivots (by user / capability / tag / day)
      //   Activity → raw event log + admin audit trail
      // Prior 4-child IA (Overview / Spend / Activity / Insights) had
      // Overview + Insights rendering the same body twice, and "Spend"
      // mislabeled a surface that also carried by-tag + by-day pivots.
      // Each name is now a noun the admin already knows from Segment /
      // Google Ads / other B2B tools — no jargon.
      // Icons: dashboard (aggregate panels) · coin (Reports is a
      // dimensional spend breakdown) · pulse (heartbeat monitor for live
      // activity). Each is unique within admin nav + distinct from the
      // parent's bar-chart glyph.
      { kind: 'leaf', label: 'Overview',  iconKind: 'dashboard',    to: '/admin/analytics',          end: true, title: 'Dashboard — spend, activity, top drivers, and optimization callouts' },
      { kind: 'leaf', label: 'Reports',   iconKind: 'token-debit',  to: '/admin/analytics/reports',  title: 'Drilldown pivots — by user, capability, tag, and day' },
      { kind: 'leaf', label: 'Activity',  iconKind: 'pulse',        to: '/admin/analytics/activity', title: 'Raw event log — dispatches and admin audit' },
    ],
  },
];

const STORAGE_PREFIX = 'admin-subnav-expanded:';

function isParentActive(parent: ParentNode, pathname: string): boolean {
  return parent.children.some((c) => pathname === c.to || pathname.startsWith(c.to + '/'));
}

function loadStoredExpanded(id: string, defaultOpen: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + id);
    if (raw === null) return defaultOpen;
    return raw === '1';
  } catch {
    return defaultOpen;
  }
}

function persistExpanded(id: string, open: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + id, open ? '1' : '0');
  } catch {
    // Storage unavailable (private mode, etc.) — accept the loss.
  }
}

export function AdminSubNav({ role }: AdminSubNavProps) {
  const location = useLocation();
  const [counts, setCounts] = useState<{ pending_invitations: number; open_requests: number } | null>(null);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    for (const node of TREE) {
      if (node.kind === 'parent') {
        const activeChild = isParentActive(node, location.pathname);
        out[node.id] = loadStoredExpanded(node.id, activeChild);
      }
    }
    return out;
  });

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const node of TREE) {
        if (node.kind === 'parent' && isParentActive(node, location.pathname) && !prev[node.id]) {
          next[node.id] = true;
          persistExpanded(node.id, true);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    fetchAdminOverview()
      .then((d) => {
        if (cancelled) return;
        setCounts({
          pending_invitations: d.counts.pending_invitations,
          open_requests: d.counts.open_requests,
        });
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          // Silent fail — sidebar still works without counts.
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleParent = useCallback((id: string) => {
    setExpanded((prev) => {
      const nextValue = !prev[id];
      persistExpanded(id, nextValue);
      return { ...prev, [id]: nextValue };
    });
  }, []);

  const leafCount = useCallback(
    (leaf: LeafNode): number => {
      if (counts === null || leaf.countKeys === undefined) return 0;
      return leaf.countKeys.reduce((sum, k) => sum + counts[k], 0);
    },
    [counts],
  );

  const visibleTree = useMemo<SidebarNode[]>(() => {
    return TREE.map((node) => {
      if (node.kind === 'leaf') {
        if (node.ownerOnly === true && role !== 'owner') return null;
        return node;
      }
      const visibleChildren = node.children.filter(
        (c) => !(c.ownerOnly === true && role !== 'owner'),
      );
      if (visibleChildren.length === 0) return null;
      return { ...node, children: visibleChildren };
    }).filter((n): n is SidebarNode => n !== null);
  }, [role]);

  // Push #17 — admin nav search. Client-side filter over a flat index of
  // nav items + tab names + aliases. When the search input has a query,
  // we render the filtered list instead of the hierarchical tree.
  const [navSearch, setNavSearch] = useState('');
  const navSearchTerm = navSearch.trim().toLowerCase();
  const NAV_SEARCH_INDEX = useMemo(() => {
    type IndexEntry = {
      label: string;
      to: string;
      keywords: string[];
      ownerOnly?: boolean;
      parent?: string;
    };
    // Push 5-post-smoke-20 (2026-07-14) — index refreshed for Workspace ▾
    // parent split: General / Usage / Billing / Notifications. Old inner
    // billing routes (buy / payment-methods / transactions) folded into the
    // single Billing child; buy-credits action still findable via the
    // separate global Cmd-K "Buy credits" action.
    const idx: IndexEntry[] = [
      { label: 'Home', to: '/admin', keywords: ['home', 'overview', 'dashboard'] },
      // Workspace parent (4 children: General / Usage / Billing / Notifications).
      { label: 'General', to: '/admin/workspace', keywords: ['workspace', 'general', 'overview', 'settings', 'preferences', 'name', 'timezone', 'default model', 'memory'], parent: 'Workspace' },
      { label: 'Usage', to: '/admin/workspace/usage', keywords: ['usage', 'team pot', 'credits', 'balance', 'monthly cap', 'monthly limit', 'auto-reload', 'auto reload', 'buy credits', 'per-user', 'spend cap', 'wallet', 'personal cap', 'tags'], parent: 'Workspace' },
      { label: 'Billing', to: '/admin/workspace/billing', keywords: ['billing', 'invoices', 'payment method', 'card', 'saved card', 'credit card', 'receipts', 'stripe'], parent: 'Workspace' },
      { label: 'Notifications', to: '/admin/workspace/notifications', keywords: ['notifications', 'recipients', 'alerts', 'email'], parent: 'Workspace' },
      // People
      { label: 'People', to: '/admin/people', keywords: ['people', 'users', 'team', 'members', 'invitations', 'approvals', 'deactivated', 'inactive'] },
      { label: 'Users', to: '/admin/people', keywords: ['users', 'everyone', 'roster', 'all users', 'members', 'team'], parent: 'People' },
      { label: 'Invitations', to: '/admin/people?tab=invitations', keywords: ['invitations', 'invites', 'invite'], parent: 'People' },
      // Sub-agent WWWWW (2026-07-31) — label aligned with the on-page tab
      // ("Requests"). Keywords still include 'approvals' + legacy variants
      // so muscle-memory search keeps working. URL uses ?tab=requests
      // (the canonical key); People.tsx normalizePeople still accepts
      // ?tab=approvals for old bookmarks.
      { label: 'Requests', to: '/admin/people?tab=requests', keywords: ['requests', 'approvals', 'awaiting approval', 'credit approvals', 'pending approvals', 'access requests', 'help', 'support'], parent: 'People' },
      { label: 'Inactive users', to: '/admin/people?status=inactive', keywords: ['deactivated', 'inactive', 'removed', 'frozen'], parent: 'People' },
      // Capabilities (D-167 order)
      { label: 'Models', to: '/admin/models', keywords: ['models', 'llm', 'ai'], parent: 'Capabilities' },
      { label: 'Connectors', to: '/admin/connectors', keywords: ['connectors', 'apps', 'integrations'], parent: 'Capabilities' },
      { label: 'Prompts', to: '/admin/prompts', keywords: ['prompts', 'knowledge', 'templates'], parent: 'Capabilities' },
      { label: 'Library', to: '/admin/library', keywords: ['library', 'documents', 'knowledge base', 'files'], parent: 'Capabilities' },
      { label: 'Market', to: '/admin/market', keywords: ['market', 'marketplace', 'catalog', 'browse'], parent: 'Capabilities' },
      // Permissions
      { label: 'Permission sets', to: '/admin/permission-sets', keywords: ['permission sets', 'roles', 'permissions', 'bundles'], parent: 'Permissions' },
      { label: 'Assignments', to: '/admin/assignments', keywords: ['assignments', 'grants', 'permissions'], parent: 'Permissions' },
      // Analytics — Sub-agent TTTTT (2026-07-31) — 3-tab IA per the
      // 60k-admin test. Prior 4-tab (Overview / Spend / Activity /
      // Insights) collapsed to 3 (Overview / Reports / Activity) —
      // Overview absorbs Insights (callouts), Reports is dimension-first
      // ("by user / capability / tag / day"), Activity keeps the raw
      // dispatch + audit log. Legacy keywords ('spend', 'insights') stay
      // routed to the new homes for muscle-memory search.
      { label: 'Overview', to: '/admin/analytics', keywords: ['analytics', 'overview', 'summary', 'dashboard', 'insights', 'callouts', 'optimization', 'top users', 'top models'], parent: 'Analytics' },
      { label: 'Reports', to: '/admin/analytics/reports', keywords: ['reports', 'spend', 'cost', 'spending', 'credits', 'by user', 'by model', 'by capability', 'by connector', 'by prompt', 'by tag', 'by day', 'pivot', 'drilldown', 'csv', 'export'], parent: 'Analytics' },
      { label: 'Activity', to: '/admin/analytics/activity', keywords: ['activity', 'runs', 'dispatches', 'ai calls', 'audit', 'admin actions', 'event log', 'compliance'], parent: 'Analytics' },
    ];
    return idx;
  }, []);

  const navSearchHits = useMemo(() => {
    if (navSearchTerm === '') return [];
    return NAV_SEARCH_INDEX.filter((e) => {
      if (e.ownerOnly === true && role !== 'owner') return false;
      if (e.label.toLowerCase().includes(navSearchTerm)) return true;
      return e.keywords.some((k) => k.toLowerCase().includes(navSearchTerm));
    }).slice(0, 12);
  }, [navSearchTerm, role, NAV_SEARCH_INDEX]);

  return (
    <nav className="admin-subnav" aria-label="Admin sections">
      {/* Push #17. admin nav search. Wayfinding only. filters the nav
         structure + tab aliases, doesn't search data. Data search lives
         in the global ⌘K modal. */}
      <div className="admin-subnav-search">
        <svg
          width="13"
          height="13"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ color: 'var(--fg-muted)', flexShrink: 0 }}
        >
          <circle cx="7" cy="7" r="4" />
          <path d="M10 10l3 3" />
        </svg>
        <input
          type="text"
          value={navSearch}
          onChange={(e) => setNavSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setNavSearch('');
            }
          }}
          placeholder="Find a setting…"
          aria-label="Search admin navigation"
        />
        {navSearch !== '' && (
          <button
            type="button"
            onClick={() => setNavSearch('')}
            aria-label="Clear search"
            title="Clear"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--fg-muted)',
              fontSize: '14px',
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* When a search query is active, render the flat filtered hits
         instead of the hierarchical tree. Esc or × restores the full nav. */}
      {navSearchTerm !== '' ? (
        navSearchHits.length === 0 ? (
          <div
            style={{
              padding: '12px 10px',
              fontSize: '12px',
              color: 'var(--fg-muted)',
              textAlign: 'center',
            }}
          >
            No nav items match "{navSearch.trim()}".
          </div>
        ) : (
          navSearchHits.map((hit) => (
            <NavLink
              key={hit.to}
              to={hit.to}
              onClick={() => setNavSearch('')}
              className="admin-subnav-link admin-subnav-link--top"
              title={hit.parent !== undefined ? `${hit.parent} → ${hit.label}` : hit.label}
            >
              <span className="admin-subnav-label">
                {hit.parent !== undefined ? (
                  <>
                    <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>{hit.parent} → </span>
                    {hit.label}
                  </>
                ) : (
                  hit.label
                )}
              </span>
            </NavLink>
          ))
        )
      ) : (
      visibleTree.map((node) => {
        const zoneClass = node.zoneStart === true ? ' admin-subnav-zone-start' : '';
        if (node.kind === 'leaf') {
          const count = leafCount(node);
          return (
            <NavLink
              key={node.to}
              to={node.to}
              {...(node.end === true ? { end: true } : {})}
              {...(node.title !== undefined ? { title: node.title } : {})}
              className={({ isActive }) =>
                `admin-subnav-link admin-subnav-link--top${isActive ? ' active' : ''}${zoneClass}`
              }
            >
              <ObjectIcon kind={node.iconKind} size="xs" tone="obsidian" />
              <span className="admin-subnav-label">{node.label}</span>
              {count > 0 && <span className="admin-subnav-link-count">{count}</span>}
            </NavLink>
          );
        }
        // Parent row
        const open = expanded[node.id] === true;
        const isActive = isParentActive(node, location.pathname);
        return (
          <div key={node.id} className={`admin-subnav-parent${zoneClass}`}>
            <button
              type="button"
              className={`admin-subnav-parent-row${isActive ? ' active' : ''}`}
              onClick={() => toggleParent(node.id)}
              aria-expanded={open}
              {...(node.title !== undefined ? { title: node.title } : {})}
            >
              <ObjectIcon kind={node.iconKind} size="xs" tone="obsidian" />
              <span className="admin-subnav-label">{node.label}</span>
              <span
                className={`admin-subnav-chevron-slot${open ? ' is-open' : ''}`}
                aria-hidden="true"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3.5 2L6.5 5L3.5 8" />
                </svg>
              </span>
            </button>
            <div
              className="admin-subnav-children"
              style={{
                // Push #33: bumped per-child height from 32 → 40 + safety
                // margin 4 → 12. The actual rendered row height is ~34px
                // (7px×2 padding + 13px×1.5 line-height), and the old
                // multiplier was clipping the last 4-ish px — symptom Ean
                // hit was the Catalog item (4th child under Capabilities)
                // looking truncated. The new headroom covers font-size /
                // density variation across browsers without animation jank.
                maxHeight: open ? `${node.children.length * 40 + 12}px` : '0px',
              }}
              role="region"
              aria-hidden={!open}
            >
              {node.children.map((c) => {
                const count = leafCount(c);
                // Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — child
                // rows render THEIR OWN ObjectIcon at size='xs' + obsidian
                // tone override (walkback of Push 32's text-only child
                // rows). Ean directive: "sub child items icons ... should
                // be unique and logical." Each child's iconKind is now
                // distinct from every other admin sub-nav child AND
                // distinct from its parent (see TREE definition above +
                // header docstring for the full mapping). Shared-concept
                // icons across app surfaces stay consistent (e.g.,
                // IconLibrary here matches user Library rail row).
                return (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    {...(c.end === true ? { end: true } : {})}
                    {...(c.title !== undefined ? { title: c.title } : {})}
                    className={({ isActive: childActive }) =>
                      `admin-subnav-link admin-subnav-link--child${childActive ? ' active' : ''}`
                    }
                    tabIndex={open ? 0 : -1}
                  >
                    <ObjectIcon kind={c.iconKind} size="xs" tone="obsidian" />
                    <span className="admin-subnav-label">{c.label}</span>
                    {count > 0 && <span className="admin-subnav-link-count">{count}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        );
      })
      )}
    </nav>
  );
}
